import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { catchError, distinctUntilChanged, map, of, take } from 'rxjs';
import { driver, DriveStep, Driver } from 'driver.js';

import { versions } from 'src/environments/versions';
import {
  Tour,
  TourSpaceRole,
  TourStep,
  TourStepDismissalType,
  TourTriggerType,
  ToursResponse,
} from '@pncl/common-models';
import { Session } from 'src/app/models/session';
import { InstitutionTheme, User } from 'src/app/models/user';
import { SpaceRepository } from 'src/app/state/space.repository';
import { DomListener } from 'src/app/utilities/DomListener';
import { LocalStorageItemIDs } from 'src/app/services/local-storage.service';
import { modifiedSetTimeout } from 'src/app/utilities/ZoneUtils';
import { DeviceAndBrowserDetectorService } from '../device-and-browser-detector.service';
import { DomListenerFactoryService } from '../dom-listener-factory.service';
import { URLService } from '../dynamic-url.service';
import { TelemetryService } from '../telemetry.service';
import { ThemeService } from '../theme.service';
import { UserService } from '../user.service';
import { preloadTourRichContent } from './tour-content';
import {
  buildDriverSteps,
  getActiveStepDriverConfig,
  getDriverConfig,
  syncRenderedPopoverStyling,
} from './tour-driverjs-config';
import {
  getDisplayedTourElements,
  getStepElementsIncludingHidden,
  hasDisplayedStepSelectors,
  hasRequiredTourSelectors,
  isHoverTriggerActive,
  isWithinHoverRegion,
  useNearestScrollIntoViewForNextCall,
} from './tour-dom';
import {
  getHoverReevaluationEventNames,
  getTriggerEventNames,
  hasTourTrigger,
  matchesTourAudience,
  matchesTourConditions,
  matchesTourTrigger,
  normalizeTourPath,
  sortTours,
  TourMatchContext,
} from './tour-matching';
import { TourStorageService } from './tour-storage';

const TOUR_START_RETRY_DELAY_MS = 3000;

// untilDestroy is not needed as this service is injected in root
@Injectable({
  providedIn: 'root',
})
export class TourService {
  private static readonly TOURS_STORAGE_KEY = LocalStorageItemIDs.TOURS_STATE;

  private driverInstance?: Driver;
  private activeTour?: Tour;
  private activeTourStep?: TourStep;
  private activeHoverPopoverElement?: HTMLElement;
  private currentInstitutionTheme?: InstitutionTheme;
  private currentUser?: User;
  private availableTours: Tour[] = [];
  private triggerEventMatch = new Set<string>();
  private hoverReevaluationEventMatch = new Set<string>();
  private bufferedTriggerEvents?: string[];
  private dismissEventMatch = new Set<string>();
  private readonly preloadedTourContent = new Map<string, Promise<void>>();
  private readonly hoverDomListener: DomListener;
  private readonly currentStepDomListener: DomListener;
  private activeStepResizeObserver?: ResizeObserver;
  private activeStepVisibilityCheckFrame?: number;
  private tourStartRetryTimeout?: ReturnType<typeof modifiedSetTimeout>;
  // Orders overlapping async start attempts so a slower earlier one cannot
  // replace the retry scheduled by a newer evaluation.
  private tourStartGeneration = 0;
  constructor(
    private readonly http: HttpClient,
    private readonly urlService: URLService,
    private readonly router: Router,
    private readonly themeService: ThemeService,
    private readonly userService: UserService,
    private readonly spaceRepo: SpaceRepository,
    private readonly telemetryService: TelemetryService,
    private readonly domListenerFactoryService: DomListenerFactoryService,
    private readonly tourStorageService: TourStorageService,
    private readonly sanitizer: DomSanitizer,
    private readonly ngZone: NgZone,
    private readonly deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
  ) {
    this.hoverDomListener = this.domListenerFactoryService.createInstance();
    this.currentStepDomListener = this.domListenerFactoryService.createInstance();

    // Keep a current snapshot of user/theme state so route and telemetry events can
    // evaluate tours synchronously without fanning back out into more streams.
    this.themeService.currentInstitutionTheme$.subscribe((theme) => {
      this.currentInstitutionTheme = theme;
    });

    this.userService.user.subscribe((userData) => {
      this.currentUser = userData?.user;
    });

    // Load the tour catalog if the user id changes
    this.userService.user
      .pipe(
        map((userData) => userData?.user?._id),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        this.loadTourCatalog();
      });

    this.telemetryService.mostRecentEvent$.subscribe((eventName) => {
      this.handleTelemetryEvent(eventName);
    });
  }

  // Route-driven tour state is reevaluated on app navigation and after the tour catalog loads.
  handleRouteChange(url = this.router.url): void {
    this.reevaluateTours({
      pagePath: url,
      shouldRecheckHoverTours: true,
      shouldResetRouteState: true,
    });
  }

  // Telemetry events can either dismiss the active step or trigger a new event-based tour.
  private handleTelemetryEvent(eventName: string): void {
    if (this.dismissEventMatch.has(eventName)) {
      this.dismissActiveStep();
      return;
    }

    if (this.bufferedTriggerEvents) {
      this.bufferedTriggerEvents.push(eventName);
    }

    const shouldRecheckHoverTours = this.hoverReevaluationEventMatch.has(eventName);
    const shouldEvaluateImmediateTours = this.triggerEventMatch.has(eventName);

    if (!shouldRecheckHoverTours && !shouldEvaluateImmediateTours) {
      return;
    }

    this.reevaluateTours({
      eventNames: [eventName],
      shouldRecheckHoverTours,
      shouldResetRouteState: false,
    });
  }

  // Fetch `/tours` once, cache the list, and build trigger-event lookup.
  private loadTourCatalog(): void {
    this.cancelTourStartRetry();
    this.availableTours = [];
    this.triggerEventMatch.clear();
    this.hoverReevaluationEventMatch.clear();
    this.preloadedTourContent.clear();
    this.bufferedTriggerEvents = [];

    this.http
      .get<ToursResponse>(`${this.urlService.getDynamicUrl()}/tours`)
      .pipe(
        map((response) => sortTours(response.tours)),
        catchError(() => of([] as Tour[])),
        take(1),
      )
      .subscribe((availableTours) => {
        const bufferedTriggerEvents = [...(this.bufferedTriggerEvents ?? [])];

        this.availableTours = availableTours;
        this.triggerEventMatch = new Set(getTriggerEventNames(availableTours));
        this.hoverReevaluationEventMatch = new Set(getHoverReevaluationEventNames(availableTours));
        this.preloadTourContent(availableTours);
        this.bufferedTriggerEvents = undefined;
        this.handleRouteChange();
        this.reevaluateTours({
          eventNames: bufferedTriggerEvents,
          shouldRecheckHoverTours: bufferedTriggerEvents.some((eventName) =>
            this.hoverReevaluationEventMatch.has(eventName),
          ),
          shouldResetRouteState: false,
        });
      });
  }

  // Reevaluate tours for the current UI state. Hover tours are refreshed explicitly
  // when route/UI changes invalidate the registered targets. Route-change tours are
  // evaluated during route reevaluation, while event-triggered tours are checked
  // against the provided telemetry event list.
  private reevaluateTours(params: {
    pagePath?: string;
    eventNames?: string[];
    shouldRecheckHoverTours: boolean;
    shouldResetRouteState: boolean;
  }): void {
    const pagePath = normalizeTourPath(params.pagePath ?? this.router.url);

    if (params.shouldResetRouteState) {
      this.clearRouteState();
    }

    const matchContext = this.getMatchContext();
    const relevantEventNames = (params.eventNames ?? []).filter((eventName) =>
      this.triggerEventMatch.has(eventName),
    );
    const hoverReevaluationEventNames = (params.eventNames ?? []).filter((eventName) =>
      this.hoverReevaluationEventMatch.has(eventName),
    );

    if (params.shouldRecheckHoverTours) {
      const hoverMatchEventNames = hoverReevaluationEventNames.length
        ? hoverReevaluationEventNames
        : [undefined];

      const hoverTriggeredTours = this.availableTours.filter(
        (tour) =>
          tour.trigger?.type === TourTriggerType.HOVER &&
          !this.tourStorageService.shouldTourBeSkippedIfAlreadyCompleted(
            TourService.TOURS_STORAGE_KEY,
            tour,
          ) &&
          hoverMatchEventNames.some((eventName) =>
            this.evaluateProductTour(tour, {
              eventName,
              pagePath,
              matchContext,
            }),
          ),
      );
      this.registerHoverTours(hoverTriggeredTours);
    }

    const matchingRouteChangeTours = this.availableTours.filter(
      (tour) =>
        tour.trigger.type === TourTriggerType.ROUTE_CHANGE &&
        this.evaluateProductTour(tour, {
          eventName: undefined,
          pagePath,
          matchContext,
        }),
    );

    const matchingEventTours = this.availableTours.filter(
      (tour) =>
        tour.trigger.type === TourTriggerType.EVENT &&
        relevantEventNames.some((eventName) =>
          this.evaluateProductTour(tour, {
            eventName,
            pagePath,
            matchContext,
          }),
        ),
    );

    // Tours have a priority order, so we need to sort them by priority and then start the highest priority tour, no matter if it's a route change or event-triggered.
    const immediateTourIds = new Set(
      [...matchingEventTours, ...matchingRouteChangeTours].map((tour) => tour._id),
    );
    const immediateTours = this.availableTours.filter((tour) => immediateTourIds.has(tour._id));

    // Never start a second tour while one is active or schedule work with no candidates.
    if (this.driverInstance?.isActive() || !immediateTours.length) {
      this.cancelTourStartRetry();
      return;
    }

    // Route evaluation can precede lazy page content, so make one bounded retry without
    // observing every DOM mutation while the page renders.
    const generation = ++this.tourStartGeneration;

    this.tryStartTour(immediateTours).then((wasStarted) => {
      if (wasStarted) {
        this.cancelTourStartRetry();
        return;
      }

      this.scheduleTourStartRetry(immediateTours, pagePath, generation);
    });
  }

  // Keep at most one delayed start attempt; only the newest evaluation may replace it.
  private scheduleTourStartRetry(tours: Tour[], pagePath: string, generation: number): void {
    // Start attempts can overlap, so an attempt that has already been superseded must
    // neither cancel the pending retry nor reinstall its own stale candidate list.
    if (generation !== this.tourStartGeneration) {
      return;
    }

    this.cancelTourStartRetry();

    if (!this.canStartTourForPath(pagePath)) {
      return;
    }

    this.tourStartRetryTimeout = modifiedSetTimeout(
      () => {
        this.tourStartRetryTimeout = undefined;

        if (!this.canStartTourForPath(pagePath)) {
          return;
        }

        this.tryStartTour(tours);
      },
      TOUR_START_RETRY_DELAY_MS,
      true,
    );
  }

  // A delayed start is stale once a tour is showing or the user has left the page
  // its candidates and steps were matched against.
  private canStartTourForPath(pagePath: string): boolean {
    return !this.driverInstance?.isActive() && normalizeTourPath(this.router.url) === pagePath;
  }

  // Clear delayed work when navigation or a successful start makes its candidates stale.
  private cancelTourStartRetry(): void {
    if (this.tourStartRetryTimeout === undefined) {
      return;
    }

    clearTimeout(this.tourStartRetryTimeout);
    this.tourStartRetryTimeout = undefined;
  }

  // This is the cheap eligibility pass. Anything that depends on DOM availability
  // or async work gets rechecked later in tryStartTour before we actually render.
  private evaluateProductTour(
    tour: Tour,
    params: {
      pagePath: string;
      eventName?: string;
      matchContext: TourMatchContext;
    },
  ): boolean {
    if (!hasTourTrigger(tour)) {
      return false;
    }

    if (!matchesTourAudience(tour.audience, params.matchContext)) {
      return false;
    }

    if (!matchesTourConditions(tour.conditions, params.pagePath, params.matchContext)) {
      return false;
    }

    if (!matchesTourTrigger(tour.trigger, params.eventName)) {
      return false;
    }

    // If the tour content is not preloaded, do not start the tour
    if (!this.isTourContentPreloadReady(tour)) {
      return false;
    }

    if (tour.trigger.type === TourTriggerType.HOVER) {
      return getDisplayedTourElements(tour.trigger.selector).length > 0;
    }

    if (
      tour.trigger.type === TourTriggerType.EVENT ||
      tour.trigger.type === TourTriggerType.ROUTE_CHANGE
    ) {
      return true;
    }

    return false;
  }

  // Tours are tried in priority order until one survives DOM and runtime revalidation.
  private async tryStartTour(tours: Tour | Tour[]): Promise<boolean> {
    const candidates = Array.isArray(tours) ? tours : [tours];

    for (const tour of candidates) {
      let wasShown = false;
      try {
        wasShown = await this.tryStartSingleTour(tour);
      } catch {
        // Skip failing tours and continue evaluating the remaining candidates.
        wasShown = false;
      }

      if (wasShown) {
        return true;
      }
    }

    return false;
  }

  // Revalidate the tour after async and DOM-sensitive checks before rendering.
  private async tryStartSingleTour(tour: Tour): Promise<boolean> {
    if (
      this.tourStorageService.shouldTourBeSkippedIfAlreadyCompleted(
        TourService.TOURS_STORAGE_KEY,
        tour,
      )
    ) {
      return false;
    }

    if (!hasRequiredTourSelectors(tour)) {
      return false;
    }

    const pagePath = normalizeTourPath(this.router.url);

    const steps = buildDriverSteps({
      tour,
      currentPath: pagePath,
      userTheme: this.currentUser?.settings?.ui_theme?.toLowerCase(),
      sanitizer: this.sanitizer,
      onHighlightStarted: (step, driverInstance) => {
        this.activeTourStep = step;
        this.updateDismissEventMatch(step);
        this.cleanupCurrentStepHandlers();
        this.registerActiveStepDismissalClickHandlers(step);
        this.registerActiveStepVisibilityObserver(tour, step);
        this.registerHoverTourStepHandlers(tour, step);
        driverInstance.setConfig({
          ...driverInstance.getConfig(),
          ...getActiveStepDriverConfig(tour, step),
        });
      },
      onNextClick: (stepIndex, driverInstance) => {
        if (stepIndex < tour.steps.length - 1) {
          driverInstance.moveTo(stepIndex + 1);
          return;
        }

        this.destroyActiveDriver({
          tour,
          driverInstance,
          shouldPersistDismissal: true,
        });
      },
      onPrevClick: (stepIndex, showDismissButton, driverInstance) => {
        if (showDismissButton) {
          this.destroyActiveDriver({
            tour,
            driverInstance,
            shouldPersistDismissal: true,
          });
          return;
        }

        if (stepIndex > 0) {
          driverInstance.moveTo(stepIndex - 1);
        }
      },
    });
    if (!steps.length) {
      return false;
    }

    await this.waitForTourContentPreload(tour);

    if (
      tour.trigger?.type === TourTriggerType.HOVER &&
      !isHoverTriggerActive(tour.trigger.selector)
    ) {
      return false;
    }

    // Another async attempt may have started while this one awaited media preload, and the
    // user may have navigated away from the page these steps were resolved against.
    if (!this.canStartTourForPath(pagePath)) {
      return false;
    }

    this.startDriver(tour, steps);
    return true;
  }

  // Seed Driver.js with the first step's visual state, then let per-step lifecycle
  // handlers update the config as the user moves through the tour.
  private startDriver(tour: Tour, steps: DriveStep[]): void {
    this.cancelTourStartRetry();
    this.ngZone.runOutsideAngular(() => {
      const driverInstance = driver({
        steps,
        ...getDriverConfig(tour, steps.length),
        onPopoverRender: (popover) => {
          const wrapper = popover.wrapper as HTMLElement | undefined;
          if (!wrapper) {
            return;
          }

          useNearestScrollIntoViewForNextCall(wrapper);

          syncRenderedPopoverStyling({
            tour,
            activeStep: this.activeTourStep,
            popover,
            institutionTheme: this.currentInstitutionTheme,
          });
          this.registerHoverPopoverHandlers(tour, wrapper);
          wrapper.tabIndex = 0;

          // Wait until Driver.js has finished rendering before moving focus and
          // measuring button visibility/state inside the popover.
          modifiedSetTimeout(() => {
            if (!wrapper.isConnected) {
              return;
            }

            syncRenderedPopoverStyling({
              tour,
              activeStep: this.activeTourStep,
              popover,
              institutionTheme: this.currentInstitutionTheme,
            });
            popover.previousButton?.blur();
            popover.nextButton?.blur();
            popover.closeButton?.blur();
            wrapper.focus({ preventScroll: true });
          }, 0);
        },
        onDestroyStarted: (_element, _step, options) => {
          this.destroyActiveDriver({
            tour,
            driverInstance: options.driver,
            shouldPersistDismissal: true,
          });
        },
        onDestroyed: () => {
          this.cleanupActiveTour();
        },
      });

      this.activeTour = tour;
      this.driverInstance = driverInstance;
      driverInstance.drive();
    });
  }

  // Hover tours stay dormant until the user actually touches a matching target.
  // Listeners are rebuilt whenever route state changes so stale selectors are dropped.
  private registerHoverTours(tours: Tour[]): void {
    this.cleanupHoverTours();

    tours.forEach((tour) => {
      const hoverSelector =
        tour.trigger?.type === TourTriggerType.HOVER ? tour.trigger.selector : undefined;

      if (!hoverSelector) {
        return;
      }

      getDisplayedTourElements(hoverSelector).forEach((element) => {
        const mouseEnterHandler = () => {
          if (!this.driverInstance?.isActive()) {
            // Fire-and-forget the hover tour.
            this.tryStartTour(tour);
          }
        };
        const mouseLeaveHandler = (event: MouseEvent) => {
          this.closeHoverTour(tour, hoverSelector, event.relatedTarget);
        };

        this.hoverDomListener.add(element, 'mouseenter', mouseEnterHandler, true);
        this.hoverDomListener.add(element, 'mouseleave', mouseLeaveHandler, true);
      });
    });
  }

  // Route changes invalidate both hover listeners and the current driver instance.
  // Active tours are closed without persisting dismissal because the user did not opt out.
  private clearRouteState(): void {
    this.cancelTourStartRetry();
    this.cleanupHoverTours();

    const driverInstance = this.driverInstance;
    if (!driverInstance) {
      return;
    }

    this.destroyActiveDriver({
      tour: this.activeTour,
      driverInstance,
      shouldPersistDismissal: false,
    });
  }

  // Reset every active-tour pointer once Driver.js has fully torn the popover down.
  private cleanupActiveTour(): void {
    this.cleanupCurrentStepHandlers();
    this.activeHoverPopoverElement = undefined;
    this.activeTour = undefined;
    this.activeTourStep = undefined;
    this.dismissEventMatch.clear();
    this.driverInstance = undefined;
  }

  // Step-scoped listeners are disposable because each highlighted step binds a new set.
  private cleanupCurrentStepHandlers(): void {
    this.currentStepDomListener.clear();
    this.activeStepResizeObserver?.disconnect();

    if (this.activeStepVisibilityCheckFrame !== undefined) {
      window.cancelAnimationFrame(this.activeStepVisibilityCheckFrame);
    }

    this.activeStepResizeObserver = undefined;
    this.activeStepVisibilityCheckFrame = undefined;
  }

  // Hover registrations are rebuilt from scratch whenever the matching tour set changes.
  private cleanupHoverTours(): void {
    this.hoverDomListener.clear();
  }

  // Some steps dismiss when the user clicks app chrome outside Driver.js.
  // These listeners are rebound on every highlighted step.
  private registerActiveStepDismissalClickHandlers(step: TourStep): void {
    const selectors = (step.dismissConfig ?? [])
      .filter(
        (
          dismissalConfig,
        ): dismissalConfig is { dismissType: TourStepDismissalType.CLICK; selector: string } =>
          dismissalConfig.dismissType === TourStepDismissalType.CLICK &&
          dismissalConfig.selector !== undefined,
      )
      .map((dismissalConfig) => dismissalConfig.selector);

    selectors.forEach((selector) => {
      getDisplayedTourElements(selector).forEach((element) => {
        const clickHandler = () => {
          this.dismissActiveStep();
        };

        this.currentStepDomListener.add(element, 'click', clickHandler, true);
      });
    });
  }

  // Track all responsive matches and close the tour only when none remain displayed.
  private registerActiveStepVisibilityObserver(tour: Tour, step: TourStep): void {
    const hoverSelector =
      tour.trigger?.type === TourTriggerType.HOVER ? tour.trigger.selector : undefined;
    // One shared reference so the observer and the resize listener schedule the same check.
    const scheduleVisibilityCheck = () =>
      this.scheduleActiveStepVisibilityCheck(tour, step, hoverSelector);

    const resizeObserver = new ResizeObserver(scheduleVisibilityCheck);
    this.activeStepResizeObserver = resizeObserver;
    // Hidden variants are observed too, so a breakpoint change that reveals one is noticed.
    getStepElementsIncludingHidden(step, hoverSelector).forEach((element) => {
      resizeObserver.observe(element);
    });

    this.currentStepDomListener.add(window, 'resize', scheduleVisibilityCheck, true);
    scheduleVisibilityCheck();
  }

  // Collapse ResizeObserver and window resize notifications into one post-layout check.
  private scheduleActiveStepVisibilityCheck(
    tour: Tour,
    step: TourStep,
    hoverSelector?: string,
  ): void {
    if (this.activeStepVisibilityCheckFrame !== undefined) {
      return;
    }

    this.activeStepVisibilityCheckFrame = window.requestAnimationFrame(() => {
      this.activeStepVisibilityCheckFrame = undefined;
      this.closeTourIfNoStepVariantDisplayed(tour, step, hoverSelector);
    });
  }

  // A responsive breakpoint can hide the highlighted variant while another stays
  // displayed, so only tear the tour down once no variant is left to anchor to.
  private closeTourIfNoStepVariantDisplayed(
    tour: Tour,
    step: TourStep,
    hoverSelector?: string,
  ): void {
    const driverInstance = this.driverInstance;

    if (
      this.activeTour?._id !== tour._id ||
      this.activeTourStep !== step ||
      !driverInstance ||
      hasDisplayedStepSelectors(step, hoverSelector)
    ) {
      return;
    }

    this.destroyActiveDriver({
      tour,
      driverInstance,
      shouldPersistDismissal: false,
    });
  }

  // Once a hover tour is open, leaving both the trigger target and the highlighted
  // element should close it immediately unless the pointer is moving into the popover.
  private registerHoverTourStepHandlers(tour: Tour, step: TourStep): void {
    const hoverSelector =
      tour.trigger?.type === TourTriggerType.HOVER ? tour.trigger.selector : undefined;

    if (!hoverSelector) {
      return;
    }

    const selectors = new Set<string>([hoverSelector, step.element]);

    selectors.forEach((selector) => {
      getDisplayedTourElements(selector).forEach((element) => {
        const mouseLeaveHandler = (event: MouseEvent) => {
          this.closeHoverTour(tour, hoverSelector, event.relatedTarget);
        };
        const clickHandler = () => {
          const driverInstance = this.driverInstance;
          if (!driverInstance || this.activeTour?._id !== tour._id) {
            return;
          }

          this.destroyActiveDriver({
            tour,
            driverInstance,
            shouldPersistDismissal: true,
          });
        };

        this.currentStepDomListener.add(element, 'mouseleave', mouseLeaveHandler, true);
        this.currentStepDomListener.add(element, 'click', clickHandler, true);
      });
    });
  }

  // Track the active popover node so hover-close logic can treat it as part of the
  // same interactive region as the trigger and highlighted elements.
  private registerHoverPopoverHandlers(tour: Tour, wrapper: HTMLElement): void {
    const hoverSelector =
      tour.trigger?.type === TourTriggerType.HOVER ? tour.trigger.selector : undefined;

    if (!hoverSelector) {
      return;
    }

    this.activeHoverPopoverElement = wrapper;

    const mouseLeaveHandler = (event: MouseEvent) => {
      this.closeHoverTour(tour, hoverSelector, event.relatedTarget);
    };

    this.currentStepDomListener.add(wrapper, 'mouseleave', mouseLeaveHandler, true);
  }

  // Close hover tours synchronously once the pointer leaves the combined hover region.
  private closeHoverTour(
    tour: Tour,
    hoverSelector: string,
    relatedTarget: EventTarget | null,
  ): void {
    const driverInstance = this.driverInstance;

    if (
      this.activeTour?._id !== tour._id ||
      !driverInstance ||
      isWithinHoverRegion({
        hoverSelector,
        activeStep: this.activeTourStep,
        activePopoverElement: this.activeHoverPopoverElement,
        target: relatedTarget,
      })
    ) {
      return;
    }

    this.destroyActiveDriver({
      tour,
      driverInstance,
      shouldPersistDismissal: false,
    });
  }

  // Persist dismissal before handing teardown back to Driver.js; the service state
  // itself is cleared from Driver.js's onDestroyed callback.
  private destroyActiveDriver(params: {
    tour: Tour | undefined;
    driverInstance: Driver;
    shouldPersistDismissal: boolean;
  }): void {
    const { driverInstance, tour, shouldPersistDismissal } = params;

    this.cleanupCurrentStepHandlers();

    if (shouldPersistDismissal && tour) {
      this.tourStorageService.persistTourDismissal(TourService.TOURS_STORAGE_KEY, tour);
    }

    driverInstance.destroy();
  }

  // Step dismissals advance multi-step tours, but the final step should fully close the driver.
  private dismissActiveStep(): boolean {
    const activeTour = this.activeTour;
    const driverInstance = this.driverInstance;

    if (!activeTour || !driverInstance) {
      return false;
    }

    if (!driverInstance.hasNextStep()) {
      this.destroyActiveDriver({
        tour: activeTour,
        driverInstance,
        shouldPersistDismissal: true,
      });
      return true;
    }

    driverInstance.moveNext();
    return true;
  }

  // Rebuild step-level dismissal event names whenever the highlighted step changes.
  private updateDismissEventMatch(step: TourStep | undefined): void {
    this.dismissEventMatch.clear();

    if (!step?.dismissConfig?.length) {
      return;
    }

    step.dismissConfig.forEach((dismissalConfig) => {
      if (
        dismissalConfig.dismissType === TourStepDismissalType.EVENT &&
        dismissalConfig.eventName
      ) {
        this.dismissEventMatch.add(dismissalConfig.eventName);
      }
    });
  }

  // Start media preloading once per tour so start attempts only wait for already-inflight work.
  private preloadTourContent(tours: Tour[]): void {
    this.preloadedTourContent.clear();

    tours.forEach((tour) => {
      if (!this.hasPreloadableTourContent(tour)) {
        return;
      }

      this.preloadedTourContent.set(
        tour._id,
        this.ngZone.runOutsideAngular(() => preloadTourRichContent(tour)).catch(() => undefined),
      );
    });
  }

  // Start attempts consume preload state but do not kick off new preload work.
  private waitForTourContentPreload(tour: Tour): Promise<void> {
    const hasPreloadableContent = this.hasPreloadableTourContent(tour);

    if (!hasPreloadableContent) {
      return Promise.resolve();
    }

    const preloadPromise = this.preloadedTourContent.get(tour._id);
    if (preloadPromise) {
      return preloadPromise;
    }

    return Promise.reject(new Error(`Missing preloaded tour content for tour "${tour._id}"`));
  }

  // A preloadable tour is start-eligible only when a preload promise is available.
  private isTourContentPreloadReady(tour: Tour): boolean {
    return !this.hasPreloadableTourContent(tour) || this.preloadedTourContent.has(tour._id);
  }

  // Image/video heroes with non-empty URLs should be considered preload-relevant.
  private hasPreloadableTourContent(tour: Tour): boolean {
    return tour.steps.some((step) => !!step.popoverContent.hero?.url);
  }

  // Collapse the current app state into the pure matching context consumed by
  // tour-matching helpers.
  private getMatchContext(): TourMatchContext {
    const releaseVersionRaw = versions.version;
    const releaseVersion =
      typeof releaseVersionRaw === 'number'
        ? releaseVersionRaw
        : typeof releaseVersionRaw === 'string'
        ? parseInt(releaseVersionRaw, 10)
        : undefined;
    const activeSpace = this.spaceRepo.activeSpace;
    const institutionId =
      activeSpace?.institutionID ??
      activeSpace?.institution?._id ??
      this.currentUser?.institution?._id;

    return {
      releaseVersion: Number.isNaN(releaseVersion) ? undefined : releaseVersion,
      institutionId,
      spaceRole: activeSpace ? this.resolveSpaceRole(activeSpace) : undefined,
      personas: this.currentUser?.personas?.length ? [...this.currentUser.personas] : undefined,
      isApiManaged: this.currentUser?.isApiManaged === true,
      isInIframe: this.deviceAndBrowserDetectorService.isInIframe(),
    };
  }

  // Tour audience rules only need a coarse host/participant/guest role, not the full session model.
  private resolveSpaceRole(space: Session): TourSpaceRole {
    if (this.currentUser && this.spaceRepo.isSpaceOwnerOrHost(this.currentUser, space)) {
      return TourSpaceRole.HOST;
    }

    if (this.currentUser?.isAnonymous) {
      return TourSpaceRole.GUEST;
    }

    return TourSpaceRole.PARTICIPANT;
  }
}
